import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/admin/vitals")({
  component: VitalsPage,
  head: () => ({
    meta: [
      { title: "Web Vitals — Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

// Thresholds per https://web.dev/articles/vitals
const THRESHOLDS: Record<string, { good: number; poor: number; unit: string }> = {
  LCP: { good: 2500, poor: 4000, unit: "ms" },
  INP: { good: 200, poor: 500, unit: "ms" },
  CLS: { good: 0.1, poor: 0.25, unit: "" },
  FCP: { good: 1800, poor: 3000, unit: "ms" },
  TTFB: { good: 800, poor: 1800, unit: "ms" },
};

function ratingOf(metric: string, p75: number): "good" | "needs-improvement" | "poor" {
  const t = THRESHOLDS[metric];
  if (!t) return "good";
  if (p75 <= t.good) return "good";
  if (p75 <= t.poor) return "needs-improvement";
  return "poor";
}

function fmt(metric: string, v: number) {
  const t = THRESHOLDS[metric];
  if (!t) return v.toFixed(2);
  return metric === "CLS" ? v.toFixed(3) : `${Math.round(v)}${t.unit}`;
}

function percentile(sorted: number[], p: number) {
  if (sorted.length === 0) return 0;
  const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
  return sorted[idx];
}

function VitalsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["web_vitals"],
    queryFn: async () => {
      const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from("web_vitals")
        .select("metric,value,path,rating,created_at")
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(10_000);
      if (error) throw error;
      return data ?? [];
    },
  });

  const summary = useMemo(() => {
    if (!data) return [];
    const buckets = new Map<string, number[]>();
    for (const row of data) {
      const arr = buckets.get(row.metric) ?? [];
      arr.push(row.value);
      buckets.set(row.metric, arr);
    }
    return ["LCP", "INP", "CLS", "FCP", "TTFB"].map((metric) => {
      const values = (buckets.get(metric) ?? []).sort((a, b) => a - b);
      const p75 = percentile(values, 75);
      return { metric, count: values.length, p75, rating: ratingOf(metric, p75) };
    });
  }, [data]);

  const perPage = useMemo(() => {
    if (!data) return [];
    const map = new Map<string, { path: string; lcp: number[]; inp: number[]; cls: number[] }>();
    for (const row of data) {
      const key = row.path;
      const bucket = map.get(key) ?? { path: key, lcp: [], inp: [], cls: [] };
      if (row.metric === "LCP") bucket.lcp.push(row.value);
      if (row.metric === "INP") bucket.inp.push(row.value);
      if (row.metric === "CLS") bucket.cls.push(row.value);
      map.set(key, bucket);
    }
    return Array.from(map.values())
      .map((b) => ({
        path: b.path,
        samples: b.lcp.length + b.inp.length + b.cls.length,
        lcp_p75: percentile(b.lcp.sort((a, c) => a - c), 75),
        inp_p75: percentile(b.inp.sort((a, c) => a - c), 75),
        cls_p75: percentile(b.cls.sort((a, c) => a - c), 75),
      }))
      .sort((a, b) => b.samples - a.samples)
      .slice(0, 50);
  }, [data]);

  return (
      <div className="mx-auto max-w-6xl space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Web Vitals — last 7 days</h1>
          <p className="text-sm text-muted-foreground">
            Real-user Core Web Vitals reported by the site. p75 is the score Google uses for
            search ranking signals.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {summary.map((s) => (
            <Card key={s.metric}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{s.metric} <span className="text-muted-foreground">p75</span></CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">{fmt(s.metric, s.p75)}</div>
                <div className="mt-2 flex items-center gap-2">
                  <Badge
                    variant={s.rating === "good" ? "default" : s.rating === "poor" ? "destructive" : "secondary"}
                  >
                    {s.rating}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{s.count} samples</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Slowest routes (p75)</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>
            ) : perPage.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                No samples yet — data will appear as visitors load pages.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Route</TableHead>
                    <TableHead className="text-right">Samples</TableHead>
                    <TableHead className="text-right">LCP</TableHead>
                    <TableHead className="text-right">INP</TableHead>
                    <TableHead className="text-right">CLS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {perPage.map((r) => (
                    <TableRow key={r.path}>
                      <TableCell className="font-mono text-xs">{r.path}</TableCell>
                      <TableCell className="text-right">{r.samples}</TableCell>
                      <TableCell className="text-right">{r.lcp_p75 ? fmt("LCP", r.lcp_p75) : "—"}</TableCell>
                      <TableCell className="text-right">{r.inp_p75 ? fmt("INP", r.inp_p75) : "—"}</TableCell>
                      <TableCell className="text-right">{r.cls_p75 ? fmt("CLS", r.cls_p75) : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
  );
}
